<template>
  <div>
    <FilterReports
      page_type="stats_dialing"
      :filter-config="filterConfig"
      :selects="selects"
      :callback="fetchRows"
      :show-period="true"
      @export="exportExcel"
    />
    <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
    <v-data-table
      :headers="headers"
      :items="displayRows"
      :loading="loading"
      :items-per-page="10"
      :page.sync="page"
      item-key="id"
      no-data-text="Нет записей"
      loading-text="Загружаем данные таблицы"
      hide-default-footer
    >
      <template #progress>
        <v-progress-linear indeterminate color="#4B5EFF" absolute top />
      </template>
      <template #[`header.group_actions`]>
        <v-btn icon small aria-label="Выбрать группировку" @click="openGrouping">
          <v-icon>mdi-cog-outline</v-icon>
        </v-btn>
      </template>
      <template #[`item.answerRate`]="{ item }">{{ percent(item.answerRate) }}</template>
      <template #[`item.realAnswerRate`]="{ item }">{{ percent(item.realAnswerRate) }}</template>
    </v-data-table>
    <v-pagination
      v-model="page"
      color="#4B5EFF"
      class="my-4"
      :total-visible="7"
      :length="Math.ceil(displayRows.length / 10)"
    />
    <v-dialog v-model="groupDialog" max-width="400" content-class="grouping-dialog">
      <v-card class="grouping-card">
        <v-card-title class="grouping-title">
          Группировка по полям
          <v-spacer />
          <v-btn icon small aria-label="Закрыть" @click="groupDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="grouping-content">
          <v-checkbox
            v-for="field in dimensions"
            :key="field.value"
            v-model="draftGrouping"
            :label="field.text"
            :value="field.value"
            color="#4B5EFF"
            hide-details
            class="grouping-checkbox"
          />
          <p v-if="!draftGrouping.length" class="mt-4">Без группировки будут показаны общие итоги.</p>
        </v-card-text>
        <v-card-actions class="grouping-actions">
          <v-btn text @click="groupDialog = false">Отмена</v-btn>
          <v-spacer />
          <v-btn color="#4B5EFF" class="apply-btn" dark @click="applyGrouping">Применить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import XLSX from 'xlsx';
import FilterReports from '@/components/Filter/ReportsFilter.vue';

const dimensions = [
  { text: 'Аккаунт', value: 'account' },
  { text: 'Транк', value: 'channel' },
  { text: 'Оператор Б-номера', value: 'operator' },
];
const metrics = [
  { text: 'Вызовы', value: 'totalCalls' },
  { text: 'Технически отвечено', value: 'answered' },
  { text: 'Реально отвечено', value: 'realAnswered' },
  { text: 'Технический процент дозвона', value: 'answerRate' },
  { text: 'Реальный процент дозвона', value: 'realAnswerRate' },
];

export default {
  name: 'DialingAnalytics',
  components: { FilterReports },
  data() {
    return {
      dimensions,
      metrics,
      grouping: dimensions.map(field => field.value),
      draftGrouping: [],
      groupDialog: false,
      rows: [],
      selects: { accounts: [], channels: [] },
      optionsPeriod: '',
      loading: false,
      error: '',
      requestId: 0,
      page: 1,
      filterConfig: [
        { type: 'select', field: 'account', label: 'Аккаунт', items: 'accounts', multiple: true },
        { type: 'select', field: 'channel', label: 'Транк', items: 'channels' },
      ],
    };
  },
  computed: {
    visibleDimensions() {
      return this.dimensions.filter(field => this.grouping.includes(field.value));
    },
    headers() {
      return [...this.visibleDimensions, ...this.metrics,
        { text: '', value: 'group_actions', sortable: false, width: '50px' }];
    },
    displayRows() {
      const groups = new Map();
      this.rows.forEach(row => {
        const key = JSON.stringify(this.grouping.map(field => row[field] || ''));
        if (!groups.has(key)) {
          const group = { id: key, totalCalls: 0, answered: 0, realAnswered: 0 };
          this.grouping.forEach(field => { group[field] = row[field] || ''; });
          groups.set(key, group);
        }
        const group = groups.get(key);
        ['totalCalls', 'answered', 'realAnswered'].forEach(field => {
          group[field] += Number(row[field]) || 0;
        });
      });
      return Array.from(groups.values()).map(group => ({
        ...group,
        answerRate: group.totalCalls ? group.answered / group.totalCalls * 100 : 0,
        realAnswerRate: group.totalCalls ? group.realAnswered / group.totalCalls * 100 : 0,
      }));
    },
  },
  methods: {
    ids(value) {
      const values = Array.isArray(value) ? value : value ? [value] : [];
      return values.map(item => typeof item === 'object'
        ? item.id ?? item.value ?? item.name ?? '' : item).filter(Boolean);
    },
    async fetchRows(filters = {}) {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      this.rows = [];
      this.page = 1;
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const payload = {
        startDate: `${filters.startDate || today}T${filters.startTime || '00:00'}`,
        endDate: `${filters.endDate || today}T${filters.endTime || '23:59'}`,
        account: this.ids(filters.account),
        channel: this.ids(filters.channel),
      };
      try {
        const response = await new Promise(resolve => {
          this.$http.post('/statistics/dialing', payload, resolve);
        });
        if (requestId !== this.requestId) return;
        if (!response || !Array.isArray(response.rows)) {
          throw new Error(response?.error || response?.message || 'Не удалось загрузить аналитику дозвона');
        }
        this.rows = response.rows;
        const period = JSON.stringify([payload.startDate, payload.endDate]);
        const toItems = (name, field) => {
          const supplied = response.selects?.[name];
          const previous = this.optionsPeriod === period ? this.selects[name] : [];
          const options = Array.isArray(supplied) && supplied.length ? supplied : previous;
          const values = new Set([
            ...options.map(item => item.id),
            ...response.rows.map(row => row[field]),
          ].filter(value => value !== null && value !== undefined && value !== ''));
          return Array.from(values).sort().map(value => ({ id: value, name: String(value) }));
        };
        this.selects = {
          accounts: toItems('accounts', 'account'),
          channels: toItems('channels', 'channel'),
        };
        this.optionsPeriod = period;
      } catch (err) {
        if (requestId === this.requestId) this.error = err.message;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    openGrouping() {
      this.draftGrouping = [...this.grouping];
      this.groupDialog = true;
    },
    applyGrouping() {
      this.grouping = this.dimensions.map(field => field.value)
        .filter(field => this.draftGrouping.includes(field));
      this.page = 1;
      this.groupDialog = false;
    },
    percent(value) {
      return `${value.toFixed(2)}%`;
    },
    exportExcel() {
      if (this.loading || !this.displayRows.length) return;
      const columns = [...this.visibleDimensions, ...this.metrics];
      const data = this.displayRows.map(row => {
        const result = {};
        columns.forEach(column => {
          result[column.text] = ['answerRate', 'realAnswerRate'].includes(column.value)
            ? this.percent(row[column.value]) : row[column.value];
        });
        return result;
      });
      const sheet = XLSX.utils.json_to_sheet(data);
      sheet['!cols'] = columns.map(column => ({ wch: Math.max(column.text.length + 2, 18) }));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Аналитика дозвона');
      XLSX.writeFile(book, 'dialing_analytics.xlsx');
    },
  },
};
</script>

